import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../ConfirmModal";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAdminSort } from "../../hooks/useAdminSort";
import SortableTh from "./shared/SortableTh";
import {
  fetchAdminUsers, banUser, unbanUser, AdminUser,
  fetchUserDeletionImpact, deleteUser, UserDeletionImpact,
} from "../../api/admin";
import { card, dangerBtn, inputStyle, smallBtn, tableStyle, tdStyle, thStyle, errorStyle, badge, panelHeading } from "../../styles";

interface Props {
  user: User;
}

const PAGE_SIZE = 25;

export default function UsersTab({ user }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sort, toggleSort } = useAdminSort({ sortBy: "displayName", sortDir: "asc" });

  const [confirmBan, setConfirmBan] = useState<AdminUser | null>(null);
  const [banLoading, setBanLoading] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<UserDeletionImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = async (s: string, p: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminUsers(user, { search: s || undefined, page: p, sortBy: sort.sortBy ?? undefined, sortDir: sort.sortDir });
      setUsers(data.users);
      setTotal(data.total);
    } catch {
      setError(t("admin.users.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("", 1);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(search, 1); }, 300);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(search, page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); load(search, 1); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBanToggle = async () => {
    if (!confirmBan) return;
    setBanLoading(true);
    try {
      if (confirmBan.banned) {
        await unbanUser(user, confirmBan.id);
      } else {
        await banUser(user, confirmBan.id);
      }
      setConfirmBan(null);
      load(search, page);
    } catch {
      setError(t("admin.users.errors.failedToSave"));
    } finally {
      setBanLoading(false);
    }
  };

  const handleDeleteClick = async (u: AdminUser) => {
    setError("");
    setImpactLoading(u.id);
    try {
      const impact = await fetchUserDeletionImpact(user, u.id);
      if (impact.adminOf.length > 0) {
        setError(
          t("admin.users.confirmDelete.blockedByLeagues", {
            name: u.displayName,
            count: impact.adminOf.length,
            leagues: impact.adminOf.map((l) => l.name).join(", "),
          })
        );
        return;
      }
      setDeleteImpact(impact);
      setConfirmDelete(u);
    } catch {
      setError(t("admin.users.errors.failedToLoad"));
    } finally {
      setImpactLoading(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setDeleteLoading(true);
    try {
      await deleteUser(user, confirmDelete.id);
      setConfirmDelete(null);
      setDeleteImpact(null);
      load(search, page);
    } catch {
      setError(t("admin.users.errors.failedToSave"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {error && <p style={errorStyle}>{error}</p>}

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {/* Card header with section label + search */}
        <div style={{ padding: "1.25rem 1.5rem 0.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <h2 style={{ ...panelHeading, margin: 0 }}>
            {t("admin.tabs.users")}
          </h2>
          <input
            style={{ ...inputStyle, width: "220px" }}
            placeholder={t("admin.users.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <p style={{ padding: "1rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <SortableTh label={t("admin.users.columns.displayName")} sortKey="displayName" activeSortBy={sort.sortBy} sortDir={sort.sortDir} onSort={toggleSort} />
                {!isMobile && <SortableTh label={t("admin.users.columns.email")} sortKey="email" activeSortBy={sort.sortBy} sortDir={sort.sortDir} onSort={toggleSort} />}
                <th style={thStyle}>{t("admin.users.columns.role")}</th>
                <th style={thStyle}>{t("admin.users.columns.status")}</th>
                {!isMobile && <SortableTh label={t("admin.users.columns.joined")} sortKey="createdAt" activeSortBy={sort.sortBy} sortDir={sort.sortDir} onSort={toggleSort} />}
                <th style={thStyle}>{t("admin.users.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ opacity: u.banned ? 0.65 : 1 }}>
                  <td style={tdStyle}>
                    {u.displayName}
                    {u.role === "ADMIN" && (
                      <span style={{ ...badge, background: "var(--color-badge-admin-bg)", color: "var(--color-badge-admin-text)", marginLeft: "0.4rem", fontSize: "0.7rem" }}>
                        {t("admin.users.role.admin")}
                      </span>
                    )}
                  </td>
                  {!isMobile && <td style={{ ...tdStyle, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>{u.email}</td>}
                  <td style={tdStyle}>
                    <span style={{ fontSize: "0.85rem", color: "var(--color-text)" }}>
                      {u.role === "ADMIN" ? t("admin.users.role.admin") : t("admin.users.role.user")}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {u.banned ? (
                      <span style={{ ...badge, background: "var(--color-status-bust-bg)", color: "var(--color-status-bust-text)" }}>{t("admin.users.status.banned")}</span>
                    ) : (
                      <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)" }}>{t("admin.users.status.active")}</span>
                    )}
                  </td>
                  {!isMobile && (
                    <td style={{ ...tdStyle, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                      {new Date(u.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </td>
                  )}
                  <td style={tdStyle}>
                    {u.role !== "ADMIN" && (
                      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "0.5rem" }}>
                        <button
                          style={u.banned ? { ...smallBtn, borderColor: "var(--color-success)", color: "var(--color-success)" } : dangerBtn}
                          onClick={() => setConfirmBan(u)}
                        >
                          {u.banned ? t("admin.users.unban") : t("admin.users.ban")}
                        </button>
                        <button
                          style={dangerBtn}
                          disabled={impactLoading === u.id}
                          onClick={() => handleDeleteClick(u)}
                        >
                          {impactLoading === u.id ? "…" : t("admin.users.delete")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination inside card footer */}
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.75rem 1.5rem", borderTop: "1px solid var(--color-border)" }}>
            <button style={smallBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t("admin.pagination.previous")}
            </button>
            <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              {t("admin.pagination.pageOf", { page, total: totalPages })}
            </span>
            <button style={smallBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              {t("admin.pagination.next")}
            </button>
          </div>
        )}
      </div>

      {confirmBan && (
        <ConfirmModal
          title={confirmBan.banned ? t("admin.users.confirmUnban.title") : t("admin.users.confirmBan.title")}
          message={
            confirmBan.banned
              ? t("admin.users.confirmUnban.message", { name: confirmBan.displayName })
              : t("admin.users.confirmBan.message", { name: confirmBan.displayName })
          }
          confirmLabel={confirmBan.banned ? t("admin.users.unban") : t("admin.users.ban")}
          danger={!confirmBan.banned}
          confirming={banLoading}
          onConfirm={handleBanToggle}
          onCancel={() => setConfirmBan(null)}
        />
      )}

      {confirmDelete && deleteImpact && (
        <ConfirmModal
          title={t("admin.users.confirmDelete.title")}
          message={
            deleteImpact.memberships.length > 0
              ? t("admin.users.confirmDelete.messageWithLeagues", {
                  name: confirmDelete.displayName,
                  count: deleteImpact.memberships.length,
                })
              : t("admin.users.confirmDelete.messageNoLeagues", { name: confirmDelete.displayName })
          }
          confirmLabel={t("admin.users.delete")}
          danger
          confirming={deleteLoading}
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setConfirmDelete(null); setDeleteImpact(null); }}
        />
      )}
    </div>
  );
}
